import type {
  ChannelKind,
  DeliveryMode,
  MonitorHistoryBucketSize,
  MonitorHistoryRange,
  Severity,
  UserRole,
} from '@meshalert/contract';

/** Shared vocabulary. The console speaks German; the API speaks its contract. */

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Kritisch',
  error: 'Fehler',
  warning: 'Warnung',
  info: 'Info',
};

export const STATUS_LABEL: Record<string, string> = {
  firing: 'Offen',
  acknowledged: 'Quittiert',
  resolved: 'Behoben',
};

export const CHANNEL_LABEL: Record<ChannelKind, string> = {
  apns_ios: 'iPhone',
  apns_macos: 'Mac',
  webhook: 'Webhook',
};

export const DELIVERY_MODE_LABEL: Record<DeliveryMode, string> = {
  silent: 'stumm',
  normal: 'normale Push',
  critical: 'Alarmton',
};

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  operator: 'Operator',
  viewer: 'Leser',
};

export const DELIVERY_STATUS_LABEL: Record<string, string> = {
  pending: 'Wartet',
  sending: 'Wird gesendet',
  delivered: 'Zugestellt',
  failed: 'Fehlgeschlagen, Retry',
  dead: 'Aufgegeben',
};

const rtf = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });

/** "vor 4 Minuten". Reads faster than a timestamp when scanning a live feed. */
export function relativeTime(iso: string): string {
  const deltaSeconds = (Date.parse(iso) - Date.now()) / 1000;
  const abs = Math.abs(deltaSeconds);

  if (abs < 45) return 'gerade eben';
  if (abs < 3600) return rtf.format(Math.round(deltaSeconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(deltaSeconds / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(deltaSeconds / 86400), 'day');
  return rtf.format(Math.round(deltaSeconds / 2592000), 'month');
}

const absoluteFormat = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function absoluteTime(iso: string): string {
  return absoluteFormat.format(new Date(iso));
}

export function shortTime(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

export function percent(value: number | null): string {
  if (value === null) return '--';
  return `${Math.round(value * 1000) / 10}%`;
}

/** Duration between two instants, as an operator would say it out loud. */
export function duration(fromIso: string, toIso: string | null): string {
  const to = toIso ? Date.parse(toIso) : Date.now();
  const seconds = Math.max(0, Math.round((to - Date.parse(fromIso)) / 1000));
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

/* ---------------------------------------------------------- monitor-verlauf */

/** Die Zone des Betrachters. Tagesbalken beginnen um seine Mitternacht. */
export const viewerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';

export const HISTORY_RANGE_LABEL: Record<MonitorHistoryRange, string> = {
  '24h': '24 Stunden',
  '7d': 'Woche',
  '30d': 'Monat',
  all: 'Gesamt',
};

/**
 * Erreichbarkeit mit zwei Nachkommastellen, abgerundet: 59 von 60 Prüfungen
 * dürfen nicht als „100 %" erscheinen.
 */
export function uptimePercent(value: number | null): string {
  if (value === null) return '--';
  const floored = Math.floor(value * 10000) / 100;
  return `${floored.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}

const weekdayTime = new Intl.DateTimeFormat('de-DE', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
const weekdayDate = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
const dayMonth = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' });
const dayMonthYear = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });

/** Was ein Balken abdeckt, so wie man es sagen würde. */
export function bucketLabel(startIso: string, endIso: string, size: MonitorHistoryBucketSize): string {
  const start = new Date(startIso);
  switch (size) {
    case '1h':
    case '6h':
      return `${weekdayTime.format(start)} bis ${shortTime(endIso)}`;
    case '1d':
      return weekdayDate.format(start);
    case '1w':
      return `${dayMonth.format(start)} bis ${dayMonthYear.format(new Date(Date.parse(endIso) - 86_400_000))}`;
  }
}

/** Linkes Ende der Zeitachse. Rechts steht immer „jetzt". */
export function historyStartLabel(range: MonitorHistoryRange, fromIso: string): string {
  switch (range) {
    case '24h':
      return 'vor 24 Stunden';
    case '7d':
      return 'vor 7 Tagen';
    case '30d':
      return 'vor 30 Tagen';
    case 'all':
      return `seit ${dayMonthYear.format(new Date(fromIso))}`;
  }
}

const checkTimeFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'medium' });

/** Mit Sekunden: bei einem Intervall von 30 Sekunden ist die Minute zu grob. */
export function checkTime(iso: string): string {
  return checkTimeFormat.format(new Date(iso));
}
